import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FieldType, LocationProp, SegmentTypeProps, TabTypeProp, TabTypePropDefault } from '../../../models';
import { ConnectedFieldHeader } from './FragmentHeader';
import { ConnectedAirportSelectorMobile } from '../../AirportSelector';
import { CMS, ABUtils } from '../../../utils';
import { FocusableFragment } from './FocusableFragment';
import { FragmentBody } from './FragmentBody';
import { NextButton } from './NextButton';
import { ConnectedAirportSelectorPrisma } from '../../AirportSelector/AirportSelectorPrisma';
import { SlideGroupContext } from '../../../components/SlideManager';
import { ErrorUtils } from '../../../utils/ErrorUtils';

export const LocationFragment = ({ id, setValue, sources, value, error, activeProduct }) => {
    const { t } = useTranslation();
    const context = useContext(SlideGroupContext);
    const [initialHeight] = React.useState(window.innerHeight);
    const [hasKeyboard, setKeyboard] = React.useState(false);
    const { isCssVariationMobile, isReduceSearchFlow } = ABUtils.getABs();
    const hasToShowErrorInput = context.getCurrent().active === FieldType.ARRIVAL && id === FieldType.ARRIVAL && isReduceSearchFlow;
    const isDone = !!value;
    const showNext = isDone && !hasKeyboard;
    const [showNextButton, setShowNextButton] = React.useState(showNext);
    const onResize = () => setKeyboard(window.innerHeight < initialHeight);
    const onSelectValueWithError = ({ value, onSelectValue }) => {
        onSelectValue(value, hasToShowErrorInput);
    };
    const getErrorMessage = (error) => {
        return hasToShowErrorInput && error?.error === 'samecity' ? t(ErrorUtils.getLocationErrorKey(error, activeProduct)) : '';
    };

    useEffect(() => {
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [onResize]);

    useEffect(() => {
        if (hasToShowErrorInput && !!value && error?.error !== 'samecity') {
            context.next();
            setShowNextButton(showNext);
        } else {
            setShowNextButton(false);
        }
    }, [error, value]);

    return (
        <FocusableFragment
            id={id}
            isDone={isDone}
            setValue={setValue}
        >
            {({ focus, onSelectValue }) => (
                <React.Fragment>
                    {isCssVariationMobile && (
                        <ConnectedAirportSelectorPrisma
                            focus={focus}
                            setValue={(value) => onSelectValueWithError({ value, onSelectValue })}
                            sources={sources}
                            value={value}
                            title={`flightsManager.selection.${id.toLowerCase()}.title`}
                            departureOrArrival={id}
                            errorMessage={getErrorMessage(error)}
                        />
                    )}
                    {!isCssVariationMobile && (
                        <React.Fragment>
                            <ConnectedFieldHeader />
                            <FragmentBody>
                                <div className='odf-text-xl odf-text-soft odf-text-left odf-space-outer-bottom-xl'>
                                    <CMS id={`flightsManager.selection.${id.toLowerCase()}.title`} />
                                </div>
                                <ConnectedAirportSelectorMobile
                                    focus={focus}
                                    setValue={(value) => onSelectValueWithError({ value, onSelectValue })}
                                    sources={sources}
                                    value={value}
                                    departureOrArrival={id}
                                    errorMessage={getErrorMessage(error)}
                                />
                            </FragmentBody>
                        </React.Fragment>
                    )}
                    { showNextButton && <NextButton loading={focus} testId="location-next-btn" /> }
                </React.Fragment>
            )}
        </FocusableFragment>
    );
};

LocationFragment.propTypes = {
    id: SegmentTypeProps.isRequired,
    setValue: PropTypes.func.isRequired,
    sources: PropTypes.array.isRequired,
    value: LocationProp,
    error: PropTypes.object,
    activeProduct: TabTypeProp,
};

LocationFragment.defaultProps = {
    value: null,
    error: '',
    activeProduct: TabTypePropDefault,
};
